package pl.nickers.match3.ui

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.size
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.layout.LayoutCoordinates
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import kotlin.math.min
import kotlin.math.roundToInt
import kotlinproject.composeapp.generated.resources.Res
import kotlinproject.composeapp.generated.resources.bomb
import org.jetbrains.compose.resources.painterResource
import pl.nickers.match3.BombPowerUpFlightUi
import pl.nickers.match3.FamiliarFlightUi
import pl.nickers.match3.GoalJellyFlightUi
import pl.nickers.match3.UI_FLIGHT_DURATION_MS
import pl.nickers.match3.ecs.PowerUpType
import pl.nickers.match3.PowerUpInfo
import androidx.compose.runtime.withFrameNanos

private val FlightIconSize = 40.dp
private const val GAP_FRACTION = 0.06f

private data class FlightEndpoints(
    val startInRoot: Offset,
    val endInRoot: Offset,
)

@Composable
internal fun GameGridFlightOverlays(
    bombFlight: BombPowerUpFlightUi?,
    goalFlights: List<GoalJellyFlightUi>,
    familiarFlights: List<FamiliarFlightUi>,
    powerUps: List<PowerUpInfo>,
    @Suppress("UNUSED_PARAMETER") padding: Dp,
    anchors: FlightAnchorRegistry,
    cols: Int,
    rows: Int,
    onClearBombPowerUpFlight: () -> Unit,
    onDismissGoalJellyFlight: (Int) -> Unit,
    onDismissFamiliarFlight: (Int) -> Unit,
) {
    val root = anchors.root ?: return
    // Parent GameGrid BoxWithConstraints uses contentAlignment = Center; without a full-size
    // wrapper, small flight boxes are centered first and offset() adds root coords on top → wrong position.
    Box(modifier = Modifier.fillMaxSize()) {
        bombFlight?.let { flight ->
            val powerUp = powerUps.firstOrNull {
                it.entityId == flight.powerUpEntityId && it.type == PowerUpType.BOMB
            }
            val gridCoords = anchors.grid
            if (powerUp != null && gridCoords != null) {
                key(flight.entityId) {
                    BombPowerUpFlightOverlay(
                        root = root,
                        buttonCoords = { anchors.bombButtons[powerUp.entityId] },
                        gridCoords = gridCoords,
                        targetRow = flight.targetRow,
                        targetCol = flight.targetCol,
                        cols = cols,
                        rows = rows,
                        onFinished = onClearBombPowerUpFlight,
                    )
                }
            }
        }

        for (gf in goalFlights) {
            val gridCoords = anchors.grid
            if (gridCoords != null) {
                key(gf.entityId) {
                    GoalJellyFlightOverlay(
                        root = root,
                        gridCoords = gridCoords,
                        goalCoords = { anchors.goalIcons[gf.goalPieceId] },
                        sourceRow = gf.row,
                        sourceCol = gf.col,
                        cols = cols,
                        rows = rows,
                        onFinished = { onDismissGoalJellyFlight(gf.entityId) },
                        bodyImage = gf.bodyImage,
                        faceImage = gf.faceImage,
                    )
                }
            }
        }

        for (ff in familiarFlights) {
            val gridCoords = anchors.grid
            if (gridCoords != null) {
                key(ff.entityId) {
                    FamiliarFlightOverlay(
                        root = root,
                        gridCoords = gridCoords,
                        familiarCoords = { anchors.familiar },
                        targetRow = ff.row,
                        targetCol = ff.col,
                        cols = cols,
                        rows = rows,
                        onFinished = { onDismissFamiliarFlight(ff.entityId) },
                        bodyImage = ff.bodyImage,
                        faceImage = ff.faceImage,
                    )
                }
            }
        }
    }
}

@Composable
private fun FamiliarFlightOverlay(
    root: LayoutCoordinates,
    gridCoords: LayoutCoordinates,
    familiarCoords: () -> LayoutCoordinates?,
    targetRow: Int,
    targetCol: Int,
    cols: Int,
    rows: Int,
    bodyImage: String,
    faceImage: String,
    onFinished: () -> Unit,
) {
    var familiar by remember { mutableStateOf<LayoutCoordinates?>(null) }
    LaunchedEffect(Unit) {
        while (true) {
            withFrameNanos { }
            val c = familiarCoords()
            if (c != null && c.isAttached) {
                familiar = c
                break
            }
        }
    }
    val f = familiar ?: return

    FlyingOverlayContent(
        root = root,
        layoutDeps = listOf(gridCoords, f),
        computeEndpoints = {
            val familiarTopLeft = root.localPositionOf(f, Offset.Zero)
            val startInRoot = Offset(
                familiarTopLeft.x + f.size.width / 2f,
                familiarTopLeft.y + f.size.height / 2f,
            )
            val gridTopLeft = root.localPositionOf(gridCoords, Offset.Zero)
            val cell = cellCenterInGridLocal(gridCoords.size, cols, rows, targetRow, targetCol)
            FlightEndpoints(startInRoot, gridTopLeft + cell)
        },
        onFinished = onFinished,
    ) {
        Box(modifier = Modifier.fillMaxSize()) {
            Image(
                painter = painterResource(bodyImage.toBodyDrawableRes()),
                contentDescription = null,
                modifier = Modifier.fillMaxSize(),
                contentScale = ContentScale.Fit,
            )
            Image(
                painter = painterResource(faceImage.toFaceDrawableRes()),
                contentDescription = null,
                modifier = Modifier.fillMaxSize(),
                contentScale = ContentScale.Fit,
            )
        }
    }
}

@Composable
private fun BombPowerUpFlightOverlay(
    root: LayoutCoordinates,
    buttonCoords: () -> LayoutCoordinates?,
    gridCoords: LayoutCoordinates,
    targetRow: Int,
    targetCol: Int,
    cols: Int,
    rows: Int,
    onFinished: () -> Unit,
) {
    var btn by remember { mutableStateOf<LayoutCoordinates?>(null) }
    LaunchedEffect(Unit) {
        while (true) {
            withFrameNanos { }
            val c = buttonCoords()
            if (c != null && c.isAttached) {
                btn = c
                break
            }
        }
    }
    val b = btn ?: return

    FlyingOverlayContent(
        root = root,
        layoutDeps = listOf(b, gridCoords),
        computeEndpoints = {
            val startTopLeft = root.localPositionOf(b, Offset.Zero)
            val startInRoot = Offset(
                startTopLeft.x + b.size.width / 2f,
                startTopLeft.y + b.size.height / 2f,
            )
            val gridTopLeft = root.localPositionOf(gridCoords, Offset.Zero)
            val cell = cellCenterInGridLocal(gridCoords.size, cols, rows, targetRow, targetCol)
            FlightEndpoints(startInRoot, gridTopLeft + cell)
        },
        onFinished = onFinished,
    ) {
        Image(
            painter = painterResource(Res.drawable.bomb),
            contentDescription = null,
            modifier = Modifier.fillMaxSize(),
            contentScale = ContentScale.Fit,
        )
    }
}

@Composable
private fun GoalJellyFlightOverlay(
    root: LayoutCoordinates,
    gridCoords: LayoutCoordinates,
    goalCoords: () -> LayoutCoordinates?,
    sourceRow: Int,
    sourceCol: Int,
    cols: Int,
    rows: Int,
    bodyImage: String,
    faceImage: String,
    onFinished: () -> Unit,
) {
    var goal by remember { mutableStateOf<LayoutCoordinates?>(null) }
    LaunchedEffect(Unit) {
        while (true) {
            withFrameNanos { }
            val c = goalCoords()
            if (c != null && c.isAttached) {
                goal = c
                break
            }
        }
    }
    val g = goal ?: return

    FlyingOverlayContent(
        root = root,
        layoutDeps = listOf(gridCoords, g),
        computeEndpoints = {
            val gridTopLeft = root.localPositionOf(gridCoords, Offset.Zero)
            val startCell = cellCenterInGridLocal(gridCoords.size, cols, rows, sourceRow, sourceCol)
            val startInRoot = gridTopLeft + startCell
            val goalTopLeft = root.localPositionOf(g, Offset.Zero)
            val endInRoot = Offset(
                goalTopLeft.x + g.size.width / 2f,
                goalTopLeft.y + g.size.height / 2f,
            )
            FlightEndpoints(startInRoot, endInRoot)
        },
        onFinished = onFinished,
    ) {
        Box(modifier = Modifier.fillMaxSize()) {
            Image(
                painter = painterResource(bodyImage.toBodyDrawableRes()),
                contentDescription = null,
                modifier = Modifier.fillMaxSize(),
                contentScale = ContentScale.Fit,
            )
            Image(
                painter = painterResource(faceImage.toFaceDrawableRes()),
                contentDescription = null,
                modifier = Modifier.fillMaxSize(),
                contentScale = ContentScale.Fit,
            )
        }
    }
}

/** Cell center offset inside the grid [Box] — same stride math as [GameGrid] layout. */
private fun cellCenterInGridLocal(gridSize: IntSize, cols: Int, rows: Int, row: Int, col: Int): Offset {
    // The grid Box is sized to exactly fit the board, so the cell size is the same
    // whether derived from width/cols or height/rows. Use the smaller to stay safe.
    val cellPx = min(
        gridSize.width.toFloat() / (cols + GAP_FRACTION * (cols - 1)),
        gridSize.height.toFloat() / (rows + GAP_FRACTION * (rows - 1)),
    )
    val gapPx = cellPx * GAP_FRACTION
    val stride = cellPx + gapPx
    val cx = col * stride + cellPx / 2f
    val cy = row * stride + cellPx / 2f
    return Offset(cx, cy)
}

@Composable
private fun FlyingOverlayContent(
    root: LayoutCoordinates,
    layoutDeps: List<LayoutCoordinates>,
    computeEndpoints: () -> FlightEndpoints,
    onFinished: () -> Unit,
    content: @Composable () -> Unit,
) {
    val density = LocalDensity.current
    val iconPx = with(density) { FlightIconSize.toPx() }

    var endpoints by remember { mutableStateOf<FlightEndpoints?>(null) }
    LaunchedEffect(root, layoutDeps) {
        while (endpoints == null) {
            withFrameNanos { }
            if (root.isAttached && layoutDeps.all { it.isAttached }) {
                endpoints = computeEndpoints()
            }
        }
    }

    val ep = endpoints ?: return
    val progress = remember(ep) { Animatable(0f) }
    val dx = ep.endInRoot.x - ep.startInRoot.x
    val dy = ep.endInRoot.y - ep.startInRoot.y

    LaunchedEffect(ep) {
        progress.snapTo(0f)
        progress.animateTo(1f, tween(durationMillis = UI_FLIGHT_DURATION_MS))
        onFinished()
    }

    Box(
        modifier = Modifier
            .offset {
                IntOffset(
                    (ep.startInRoot.x + dx * progress.value - iconPx / 2f).roundToInt(),
                    (ep.startInRoot.y + dy * progress.value - iconPx / 2f).roundToInt(),
                )
            }
            .size(FlightIconSize)
            .alpha(0.55f),
    ) {
        content()
    }
}

internal fun Modifier.flightRootAnchor(registry: FlightAnchorRegistry): Modifier =
    onGloballyPositioned { registry.root = it }

internal fun Modifier.flightGridAnchor(registry: FlightAnchorRegistry): Modifier =
    onGloballyPositioned { registry.grid = it }
